
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { paymentService } from '../../../services/paymentService';

const PostingOptions = ({ formData, onChange, errors }) => {
  const postingDetails = paymentService?.getPostingTypeDetails();

  const durationOptions = [
    { value: '15', label: '15 Days' },
    { value: '30', label: '30 Days (Recommended)' },
    { value: '45', label: '45 Days' },
    { value: '60', label: '60 Days' }
  ];

  const socialPlatforms = [
    { id: 'linkedin', label: 'LinkedIn', icon: 'Linkedin', enabled: false },
    { id: 'twitter', label: 'Twitter/X', icon: 'Twitter', enabled: false },
    { id: 'facebook', label: 'Facebook', icon: 'Facebook', enabled: false }
  ];

  const toggleSocialPlatform = (platformId) => {
    const platforms = formData?.socialPlatforms || socialPlatforms;
    const updated = platforms?.map(p => 
      p?.id === platformId ? { ...p, enabled: !p?.enabled } : p
    );
    onChange('socialPlatforms', updated);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">Posting Options</h2>
        <p className="text-sm text-muted-foreground">
          Choose how you want to promote your job listing and maximize visibility
        </p>
      </div>
      {/* Posting Type Selection */}
      <div className="space-y-4">
        <label className="text-sm font-medium text-foreground">
          Select Posting Type <span className="text-destructive">*</span>
        </label>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(postingDetails)?.map(([type, details]) => (
            <div
              key={type}
              onClick={() => onChange('postingType', type)}
              className={`
                relative rounded-lg border-2 p-5 cursor-pointer transition-all
                ${formData?.postingType === type
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border bg-background hover:border-primary/50 hover:shadow-sm'
                }
                ${details?.recommended ? 'ring-2 ring-primary/20' : ''}
              `}
            >
              {details?.recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    Recommended
                  </span>
                </div>
              )}

              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">{details?.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-primary">
                      {paymentService?.formatAmount(details?.price)}
                    </span>
                    {details?.price > 0 && (
                      <span className="text-sm text-muted-foreground">one-time</span>
                    )}
                  </div>
                </div>
                <div className={`
                  w-6 h-6 rounded-full border-2 flex items-center justify-center
                  ${formData?.postingType === type
                    ? 'border-primary bg-primary' :'border-border bg-background'
                  }
                `}>
                  {formData?.postingType === type && (
                    <Icon name="Check" size={14} color="white" />
                  )}
                </div>
              </div>

              <ul className="space-y-2">
                {details?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {errors?.postingType && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            {errors?.postingType}
          </p>
        )}
      </div>
      {/* Payment Notice */}
      {formData?.postingType && formData?.postingType !== 'standard' && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Payment Required</h4>
              <p className="text-sm text-muted-foreground">
                You will be redirected to complete payment of {paymentService?.formatAmount(postingDetails?.[formData?.postingType]?.price)} after reviewing your job posting. 
                Your job will be published immediately after successful payment.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Duration Selection */}
      <Select
        label="Posting Duration"
        options={durationOptions}
        value={formData?.duration}
        onChange={(value) => onChange('duration', value)}
        error={errors?.duration}
        required
        placeholder="Select duration"
        description="How long should this job posting remain active"
      />
      {/* Social Media Promotion */}
      {(formData?.postingType === 'featured' || formData?.postingType === 'premium') && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">Social Media Promotion</label>
            <Button
              variant="ghost"
              size="sm"
              iconName={formData?.showPricingDetails ? 'ChevronUp' : 'ChevronDown'}
              iconPosition="right"
              onClick={() => onChange('showPricingDetails', !formData?.showPricingDetails)}
            >
              Pricing Details
            </Button>
          </div>

          {formData?.showPricingDetails && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base posting fee:</span>
                <span className="font-medium text-foreground">
                  {formData?.postingType === 'featured' ? '₹2,999' : '₹4,999'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Social media promotion:</span>
                <span className="font-medium text-foreground">Included</span>
              </div>
              <div className="pt-2 border-t border-border flex justify-between">
                <span className="font-medium text-foreground">Total:</span>
                <span className="font-semibold text-primary">
                  {formData?.postingType === 'featured' ? '₹2,999' : '₹4,999'}
                </span>
              </div>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            Select platforms where you want to promote this job posting
          </p>
          <div className="space-y-3">
            {(formData?.socialPlatforms || socialPlatforms)?.map((platform) => (
              <div key={platform?.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <Checkbox
                  label={
                    <div className="flex items-center gap-2">
                      <Icon name={platform?.icon} size={18} />
                      <span>{platform?.label}</span>
                    </div>
                  }
                  checked={platform?.enabled}
                  onChange={() => toggleSocialPlatform(platform?.id)}
                />
                {platform?.enabled && (
                  <span className="text-xs text-success font-medium">Active</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Additional Options */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">Additional Options</label>
        <Checkbox
          label="Make this an urgent hiring position"
          description="Display 'Urgent Hiring' badge to attract immediate applications"
          checked={formData?.urgentHiring}
          onChange={(e) => onChange('urgentHiring', e?.target?.checked)}
        />
        <Checkbox
          label="Hide company name until application"
          description="Company details will be revealed only after candidate applies"
          checked={formData?.hideCompany}
          onChange={(e) => onChange('hideCompany', e?.target?.checked)}
        />
      </div>
      {/* Maximize Reach */}
      <div className="bg-success/5 border border-success/20 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="TrendingUp" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
          <div className="space-y-1 text-sm">
            <p className="font-medium text-foreground">Maximize Your Reach</p>
            <p className="text-muted-foreground">
              Featured and Premium postings receive 3-5x more applications and fill positions 40% faster than standard listings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostingOptions;